import * as React from 'react';

import { SessionWrapper } from '../../components/SessionWrapper';
import UrlComp from '../../components/UrlBar';
import { appStore } from '../../stores/AppStore';
import { errorMessageStore } from '../../stores/ErrorMessageStore';
import { FileNotFoundError, UnsupportedContentTypeError } from '../../utils/ErrorHandler';

import { developmentMode, getEnvValue } from '@egr/xbox/utils/ReactScriptHelper';
import { addBreadcrumb } from '@egr/xbox/utils/errors/Breadcrumb';
import { captureException } from '@egr/xbox/utils/errors/Error';
import { Logger } from '@egr/xbox/utils/errors/Logger';

const LazyViewerComponents = {
    ModelViewer: React.lazy(() => import('../ViewerGlb/ViewerModelViewer')),
    Babylon: React.lazy(() => import('../ViewerBabylon/ViewerBabylon')),
    Three: React.lazy(() => import('../ViewerThree/ViewerThree')),
    Marzipano: React.lazy(() => import('../ViewerMarzipano/ViewerMarzipano')),
    Krpano: React.lazy(() => import('../ViewerKrpano')),
    Ar: React.lazy(() => import('../ViewerAr/ViewerAR')),
};

function getGlbViewer(): React.ReactNode {
    const loader = getEnvValue('GLBLOADER', ['babylon', 'google', 'three'], 'babylon');
    switch (loader) {
        case 'babylon':
            return <LazyViewerComponents.Babylon />;
        case 'google':
            return <LazyViewerComponents.ModelViewer />;
        case 'three':
            return <LazyViewerComponents.Three />;
        default:
            return <LazyViewerComponents.ModelViewer />;
    }
}

function getViewerComponent(fileExtension: string): React.ReactNode {
    switch (fileExtension) {
        case 'jpg':
        case 'jpeg':
        case 'png':
            return <LazyViewerComponents.Krpano />;
        case 'pbox':
        case 'pec':
            return (
                <SessionWrapper>
                    <LazyViewerComponents.Babylon />
                </SessionWrapper>
            );
        case 'eimg':
        case 'epan':
            return <LazyViewerComponents.Marzipano />;
        case 'gltf':
        case 'glb':
            return getGlbViewer();
        case 'iar':
            return <LazyViewerComponents.Ar />;
        default:
            const error = new UnsupportedContentTypeError();
            errorMessageStore.open(error);
            addBreadcrumb({
                category: 'UnsupportedContent',
                message: fileExtension,
            });
            captureException(new Error('Content type not supported'), 'debug');
            return null;
    }
}

export function useGetViewerComponent(): React.ReactNode {
    const { urlInfo } = appStore.useState(['urlInfo']);

    if (!urlInfo) {
        return <UrlComp />;
    }

    if (developmentMode) {
        Logger.log('fileExtension: ', urlInfo.extension);
    }

    if (urlInfo.arFile) {
        return <LazyViewerComponents.Ar />;
    }

    const ext = urlInfo.extension?.toLowerCase() ?? '';
    if (ext === '') {
        errorMessageStore.open(new FileNotFoundError());
        return null;
    }
    const component = getViewerComponent(ext);

    return component;
}
